import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusPill } from "@/admin/ui";
import { money } from "@/lib/brand";
import { toast } from "sonner";
import { Download, Check, X, Ban } from "lucide-react";

export default function DownloadsTab({ clientId }) {
  const [reqs, setReqs] = useState([]);
  const [approve, setApprove] = useState(null);
  const [price, setPrice] = useState("0");
  const [terms, setTerms] = useState(false);

  const load = () => api.get(`/clients/${clientId}/download-requests`).then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clientId]);

  const doApprove = () => {
    api.post(`/download-requests/${approve.request_id}/approve`, { price: parseFloat(price) || 0, requires_terms: terms })
      .then(() => { toast.success("Request approved"); setApprove(null); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  };
  const reject = (id) => api.post(`/download-requests/${id}/reject`, { reason: "Not eligible" }).then(() => { toast.success("Rejected"); load(); }).catch(() => toast.error("Failed"));
  const revoke = (id) => { if (!window.confirm("Revoke this download? The link stops working.")) return; api.post(`/download-requests/${id}/revoke`).then(() => { toast.success("Revoked"); load(); }).catch(() => toast.error("Failed")); };

  const openApprove = (r) => { setApprove(r); setPrice(String(r.price || 0)); setTerms(!!r.requires_terms); };

  return (
    <div className="bg-white border border-[#E2EBF0] rounded-xl p-5" data-testid="client-downloads">
      <div className="flex items-center gap-2 mb-3"><Download className="h-4 w-4 text-[#10B981]" />
        <h3 className="font-heading font-semibold text-[#0D3B2D]">Download requests</h3></div>
      {reqs.length === 0 ? <p className="text-sm text-[#4A635A]">No download requests from this client.</p> : (
        <ul className="space-y-2">
          {reqs.map((r) => (
            <li key={r.request_id} className="flex items-center justify-between gap-3 border border-[#E2EBF0] rounded-lg px-3 py-2.5" data-testid={`dl-req-${r.request_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-[#0D3B2D] truncate">{r.file_name}</span>
                  <StatusPill status={r.status} />
                  {r.price > 0 && <span className="text-[11px] font-mono text-[#0D3B2D]">{money(r.price)}{r.paid ? " · paid" : " · unpaid"}</span>}
                  {r.requires_terms && <span className="text-[11px] text-[#4A635A]">{r.terms_accepted ? "terms ✓" : "terms pending"}</span>}
                </div>
                {r.reason && <p className="text-[11px] text-[#4A635A] mt-0.5 truncate">“{r.reason}”</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                {!["ready", "downloaded", "revoked", "rejected"].includes(r.status) && (
                  <>
                    <button onClick={() => openApprove(r)} data-testid={`approve-${r.request_id}`} className="text-[#10B981] text-xs inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />Approve</button>
                    <button onClick={() => reject(r.request_id)} data-testid={`reject-${r.request_id}`} className="text-red-600 text-xs inline-flex items-center gap-1"><X className="h-3.5 w-3.5" />Reject</button>
                  </>
                )}
                {["ready", "downloaded"].includes(r.status) && (
                  <button onClick={() => revoke(r.request_id)} data-testid={`revoke-${r.request_id}`} className="text-red-600 text-xs inline-flex items-center gap-1"><Ban className="h-3.5 w-3.5" />Revoke</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!approve} onOpenChange={(o) => !o && setApprove(null)}>
        <DialogContent className="sm:max-w-md" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0D3B2D]">Approve download</DialogTitle>
            <DialogDescription className="text-[#4A635A]">Set the release fee (0 = free). A paid release stays locked until the client pays via Stripe.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Release fee (USD)</Label><Input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="approve-price-input" className="bg-white border-[#E2EBF0] font-mono" /></div>
            <label className="flex items-center gap-2 text-sm text-[#0D3B2D]">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="approve-terms-check" />
              Require transfer terms acceptance
            </label>
            <Button onClick={doApprove} data-testid="approve-confirm-btn" className="w-full bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg">Approve release</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
